import {
  buildCanonicalQuery,
  getPropertyTypeLabels,
  humanizeSlug,
  parseSearchParams,
  trimTo,
} from './seoTextUtils';

const SEARCH_PATHS = {
  apartment: '/apts-search-screen',
  villa: '/villas-search-screen',
  plot: '/plots-search-screen',
};

const MAP_PATHS = {
  apartment: '/apts-location-page',
  villa: '/villas-location-page-map2',
  plot: '/plots-location-page-map2',
};

const HOME_PATHS = {
  apartment: '/apts-home',
  villa: '/villas-home',
  plot: '/plots-home',
};

const NEAR_ME_PATHS = {
  apartment: '/apts-user-location',
  villa: '/villas-user-location',
  plot: '/plots-user-location',
};

function typeParamFor(propertyType) {
  if (propertyType === 'villa') return 'villa_buy';
  if (propertyType === 'plot') return 'plot_buy';
  return 'apartment_buy';
}

export function buildSearchScreenMeta({
  propertyType = 'apartment',
  location = '',
  city = '',
  resultCount = null,
  typeParam,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const loc = humanizeSlug(location) || String(location || '').trim();
  const c = humanizeSlug(city) || String(city || '').trim() || 'Hyderabad';
  const place = loc ? `${loc}, ${c}` : c;
  const countPart = resultCount != null && resultCount > 0 ? `${resultCount}+ ` : '';
  const tp = typeParam || typeParamFor(propertyType);

  const title = trimTo(
    `${labels.capital} in ${place} — ${countPart}projects | Proppins`,
    70,
  );
  const description = trimTo(
    `Find ${labels.plural} for sale in ${place}. Filter by BHK, budget & possession. Compare projects on map & list view on Proppins.`,
    160,
  );

  const canonicalPath = `${SEARCH_PATHS[propertyType] || SEARCH_PATHS.apartment}${buildCanonicalQuery({
    type: tp,
    location: loc ? location : undefined,
    city: c,
  })}`;

  return { title, description, canonicalPath };
}

export function buildMapPageMeta({
  propertyType = 'apartment',
  location = '',
  city = '',
  typeParam,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const loc = humanizeSlug(location) || String(location || '').trim();
  const c = humanizeSlug(city) || String(city || '').trim() || 'Hyderabad';
  const tp = typeParam || typeParamFor(propertyType);

  const title = trimTo(`${loc || c} ${labels.plural} on map — ${c} | Proppins`, 70);
  const description = trimTo(
    `View ${labels.plural} projects in ${loc ? `${loc}, ${c}` : c} on an interactive map. Filter by price, BHK & distance. Open any project for full details on Proppins.`,
    160,
  );

  const canonicalPath = `${MAP_PATHS[propertyType] || MAP_PATHS.apartment}${buildCanonicalQuery({
    type: tp,
    location: loc ? location : undefined,
    city: c,
  })}`;

  return { title, description, canonicalPath };
}

export function buildNearMeMeta({
  propertyType = 'apartment',
  city = '',
  typeParam,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const c = humanizeSlug(city) || String(city || '').trim() || 'Hyderabad';
  const tp = typeParam || typeParamFor(propertyType);

  const title = trimTo(`Nearby ${labels.plural} in ${c} | Proppins`, 70);
  const description = trimTo(
    `Discover ${labels.plural} near your current location in ${c}. View projects on map, filter by distance, and explore full details on Proppins.`,
    160,
  );

  const canonicalPath = `${NEAR_ME_PATHS[propertyType] || NEAR_ME_PATHS.apartment}${buildCanonicalQuery({
    type: tp,
    city: c,
    radius_km: '5',
  })}`;

  return { title, description, canonicalPath };
}

export function buildCategoryBrowseMeta({
  propertyType = 'apartment',
  categoryName = '',
  categoryLabel = '',
  city = '',
  typeParam,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const cat =
    String(categoryLabel || '').trim() ||
    humanizeSlug(categoryName) ||
    String(categoryName || '').trim();
  const c = humanizeSlug(city) || String(city || '').trim() || 'Hyderabad';
  const tp = typeParam || typeParamFor(propertyType);

  const title = trimTo(`${cat} ${labels.plural} in ${c} | Proppins`, 70);
  const description = trimTo(
    `Browse ${cat} ${labels.plural} projects in ${c}. New launches, pricing, sizes & builder info in one place on Proppins.`,
    160,
  );

  const canonicalPath = `${HOME_PATHS[propertyType] || HOME_PATHS.apartment}${buildCanonicalQuery({
    type: tp,
    categoryname: categoryName || cat.toLowerCase().replace(/\s+/g, '-'),
    city: c,
  })}`;

  return { title, description, canonicalPath };
}

export function buildResaleSeoMeta({
  title,
  locality,
  city,
  priceRange,
  propertyType = 'apartment',
  slug,
  typeParam,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const place =
    locality && city
      ? `${humanizeSlug(locality)}, ${humanizeSlug(city)}`
      : humanizeSlug(city) || 'India';
  const name = String(title || `${labels.singular} resale`).trim();
  const pricePart = priceRange ? ` from ${String(priceRange).trim()}` : '';

  const prefix =
    propertyType === 'apartment'
      ? '/apts-resale-property-page'
      : propertyType === 'villa'
        ? '/villas-resale-property-page'
        : '/plots-resale-property-page';

  return {
    title: trimTo(`${name} in ${place} | Proppins`, 70),
    description: trimTo(
      `${name} for resale in ${place}.${pricePart}. View photos, details & contact seller on Proppins.`,
      160,
    ),
    canonicalPath: slug
      ? `${prefix}/${slug}${buildCanonicalQuery({ type: typeParam || typeParamFor(propertyType), city })}`
      : prefix,
  };
}

export function buildRentSeoMeta({
  title,
  locality,
  city,
  rentAmount,
  propertyType = 'apartment',
  slug,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const place =
    locality && city
      ? `${humanizeSlug(locality)}, ${humanizeSlug(city)}`
      : humanizeSlug(city) || 'India';
  const name = String(title || `${labels.singular} for rent`).trim();
  const rentPart = rentAmount ? ` — ${String(rentAmount).trim()}` : '';

  const prefix =
    propertyType === 'villa' ? '/rents-villas-property-page' : '/rents-apts-property-page';

  return {
    title: trimTo(`${name} for rent in ${place}${rentPart} | Proppins`, 70),
    description: trimTo(
      `Rent ${name} in ${place}.${rentPart ? ` ${rentPart.replace(/^ — /, '')} rent.` : ''} View photos, amenities & contact owner on Proppins.`,
      160,
    ),
    canonicalPath: slug ? `${prefix}/${slug}${buildCanonicalQuery({ city })}` : prefix,
  };
}

export function buildBuilderHubMeta({
  builderName,
  propertyType = 'apartment',
  city = '',
  projectCount = null,
}) {
  const labels = getPropertyTypeLabels(propertyType);
  const builder = String(builderName || 'Builder').trim();
  const c = humanizeSlug(city) || String(city || '').trim() || 'Hyderabad';
  const countPart = projectCount != null && projectCount > 0 ? ` — ${projectCount}+ projects` : '';

  return {
    title: trimTo(`${builder} ${labels.plural} in ${c}${countPart} | Proppins`, 70),
    description: trimTo(
      `Explore ${labels.plural} by ${builder} in ${c}. Compare prices, configurations, and contact the builder on Proppins.`,
      160,
    ),
    canonicalPath: '',
  };
}

export function metaFromLocationSearch(pathname, search = '') {
  const qs = parseSearchParams(search);
  const path = String(pathname || '');
  const location = (qs.get('location') || '').trim();
  const city = (qs.get('city') || '').trim();
  const typeParam = (qs.get('type') || '').trim();

  let propertyType = 'apartment';
  if (path.includes('/villas') || path.includes('/villa')) propertyType = 'villa';
  else if (path.includes('/plots') || path.includes('/plot')) propertyType = 'plot';

  if (path.includes('-user-location')) {
    return buildNearMeMeta({ propertyType, city, typeParam });
  }
  if (path.includes('-location-page')) {
    return buildMapPageMeta({ propertyType, location, city, typeParam });
  }
  if (path.includes('-search-screen')) {
    return buildSearchScreenMeta({ propertyType, location, city, typeParam });
  }

  const categoryName = (qs.get('categoryname') || qs.get('category') || '').trim();
  if (categoryName && path.includes('-home')) {
    return buildCategoryBrowseMeta({ propertyType, categoryName, city, typeParam });
  }

  return null;
}
